import { app } from '../../app';
import { PositionType } from '../../../lib/defs/types';
import {
  BasicBlockProps,
  BlockId,
  ColorId,
  CubeBlockProps,
} from '../../defs/block';
import {
  BlockEntity,
  despawnBlockEntity,
  spawnBlockEntity,
} from '../../entities/BlockEntity';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { IEffect } from './IEffect';
import { waitFor } from '../../../lib/util/waitFor';
import { BreakEffect } from './BreakEffect';
import { MergeEffect } from './MergeEffect';
import {
  Match2PowerupType,
  match2PowerupsObj,
} from 'src/replicant/match2/match2.ruleset';
import StateObserver, { user } from 'src/StateObserver';
import { getCubeTargets, getCubeTargetGroups } from '../../util/matchTools';
import { SuperCubeSingleAnimation } from '../../animations/SuperCubeSingleAnimation';

type BlockReplacer = () => BlockId;

export type SuperCubeReplaceComboEffectOptions = {
  subject: BlockEntity;
  pair: BlockEntity[];
  all: BlockEntity[];
  replacer: BlockReplacer;
};

export class SuperCubeReplaceComboEffect implements IEffect {
  private readonly scene: GameScene;
  private readonly options: SuperCubeReplaceComboEffectOptions;
  private spawedBlocks: BlockEntity[] = [];

  constructor(scene: GameScene, options: SuperCubeReplaceComboEffectOptions) {
    this.scene = scene;
    this.options = options;
  }

  public async execute() {
    const position = this.options.subject.c.position.mapPosition;
    this.spawedBlocks = [];

    // get cube color
    const color = this.getCubeColor();

    // get basic blocks of given cube color
    const targets = getCubeTargets(this.scene, color);

    // Add current chain to targets.
    targets.push(...this.options.pair);

    // merge all blocks
    await new MergeEffect(this.scene, this.options.all, position).execute();

    // animate
    await this.animate(position, targets, color);

    // replace target blocks
    await this.collideTargets(targets);

    await StateObserver.invoke.addMatch2RaceScore({
      goal: 'lollipop',
      score: targets.length,
    });
  }

  private onBeamReachTarget(target: BlockEntity) {
    new BreakEffect(this.scene, {
      position: target.c.position.mapPosition,
      size: target.c.block.props,
      color: 0xffffff,
    }).execute();
    despawnBlockEntity(this.scene, target);

    app.sound.play('button-power', {
      dupes: 2,
    });

    const spawnedEntity = spawnBlockEntity(
      this.scene,
      { id: this.options.replacer() },
      target.c.position.mapPosition,
    );

    // Add spawned block to list
    this.spawedBlocks.push(spawnedEntity);

    // Record power block created
    if (spawnedEntity.c.block?.props?.type in match2PowerupsObj) {
      this.scene.recordPowerBlockCreated(
        spawnedEntity.c.block.props.type as Match2PowerupType,
      );
    }
  }

  private async collideTargets(targets: BlockEntity[]) {
    for (const spawnedEntity of this.spawedBlocks) {
      // Collide one by one, in sequence, await only if they have a parent (not been dismissed yet)
      if (spawnedEntity.c.view2d.view.parent) {
        await waitFor(200);
      }
      spawnedEntity.c.block.collide('attack');
    }
  }

  private async animate(
    position: PositionType,
    targets: BlockEntity[],
    color: ColorId,
  ) {
    // play sound
    app.sound.play('puzzle-combo-cube-replace', {
      rate: 1.4,
      pan: mapGetPan(position),
    });

    // create animation
    const animation = new SuperCubeSingleAnimation({
      color,
      position,
      targets,
    });
    animation.onBeamReachTarget = (target) => this.onBeamReachTarget(target);

    // spawn animation as overlay entity
    const entity = spawnOverlayEntity(
      this.scene,
      position,
      { width: 1, height: 1 },
      () => animation,
      'foreground',
    );

    await animation.start();

    const targetGroups = getCubeTargetGroups(targets);

    for (const targetGroup of targetGroups) {
      await animation.fireBeamsAtTargets(targetGroup);
      animation.clearBeams();
    }

    animation.explode().then(() => {
      animation.stop();
      despawnOverlayEntity(this.scene, entity);
    });
  }

  private getCubeColor(): ColorId {
    const pair = this.options.pair;
    const props1 = pair[0].c.block.props;
    const props2 = pair[1].c.block.props;

    // get cube color
    return props1.type === 'cube'
      ? props1.color
      : (props2 as CubeBlockProps).color;
  }
}
